import React, { useState } from "react";
import { withAuthenticationRequired } from "@auth0/auth0-react";
import { Table, Button } from "reactstrap";
import {
  Loading, WarningAlert, PaginationControls, ItemsPerPageSelector, SortableHeaderCell, EntityRow, ConfirmationModal,
  NotificationToast, SimpleFormModal, SearchBar, InformationalModal
} from "../components";
import useSortableData from "../hooks/useSortableData";
import { useEntity } from "../hooks/useEntity";
import { devicesApi } from "../api/entitiesApi";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { deviceFields, deviceDefaultValues } from "../utils/entityFormFields";
import { Link } from "react-router-dom";

// styles
import "../App.css";

export function DevicesComponent() {
  const { data: devices, isLoading, error, setData: setDevices, setError } = useEntity(devicesApi);
  const { items: sortedDevices, requestSort, sortConfig } = useSortableData(devices, { key: '', direction: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const [devicesPerPage, setDevicesPerPage] = useState(50);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [currentForm, setCurrentForm] = useState(null);
  const [deleteData, setDeleteData] = useState({ open: false, deviceId: null });
  const [notification, setNotification] = useState({ visible: false, title: "", message: "" });
  const [searchTerm, setSearchTerm] = useState("");
  const [errorMessage, setErrorMessage] = useState({ open: false, title: "", message: "" });
  const [deviceType, setDeviceType] = useState(
    currentForm?.data?.device_type ?? deviceDefaultValues.device_type
  );

  const totalPages = Math.ceil(devices.length / devicesPerPage);
  const indexOfLastDevice = currentPage * devicesPerPage;
  const indexOfFirstDevice = indexOfLastDevice - devicesPerPage;
  const itemsPerPage = [50, 100];
  const filteredDevices = sortedDevices.filter(device => {
    if (!searchTerm) return true;
    const lowerTerm = searchTerm.toLowerCase();
    return (
      (device.display_name && device.display_name.toString().toLowerCase().includes(lowerTerm)) ||
      (device.id && device.id.toString().toLowerCase().includes(lowerTerm)) ||
      (device.customer_id && device.customer_id.toString().toLowerCase().includes(lowerTerm)) ||
      (device.site_id && device.site_id.toString().toLowerCase().includes(lowerTerm)) ||
      (device.device_type && device.device_type.toString().toLowerCase().includes(lowerTerm)) ||
      (device.brand_and_family && device.brand_and_family.toString().toLowerCase().includes(lowerTerm)) ||
      (device.model && device.model.toString().toLowerCase().includes(lowerTerm)) ||
      (device.serial_nr && device.serial_nr.toString().toLowerCase().includes(lowerTerm)) ||
      (device.local_ip && device.local_ip.toString().toLowerCase().includes(lowerTerm))
    );
  });
  const currentDevices = filteredDevices.slice(indexOfFirstDevice, indexOfLastDevice);
  function toggleDropdown() { setDropdownOpen(prevState => !prevState); }
  function handlePageChange(pageNumber) { setCurrentPage(pageNumber); }
  function handleDropdownChange(number) {
    setDevicesPerPage(number);
    setCurrentPage(1);
  };

  function handleEdit(device) {
    setDeviceType(device.device_type ?? deviceDefaultValues.device_type);
    setCurrentForm({ mode: "edit", data: device });
  };

  function handleAdd() {
    setCurrentForm({ mode: "add", data: { customer_id: "", site_id: "" } });
  }

  async function handleSaveForm(formData) {
    try {
      if (currentForm.mode === "edit") {
        await devicesApi.updateEntity(formData.id, formData);
        setDevices(prevData =>
          prevData.map(device =>
            device.id === formData.id ? formData : device
          )
        );
        setNotification({ visible: true, title: "Device Edited", message: "1 Device has been edited" });
        setTimeout(() => {
          setNotification({ visible: false, title: "", message: "" });
        }, 2000);
      } else {
        const newDevice = await devicesApi.addEntity(formData);
        setDevices(prevData => [...prevData, newDevice]);
        setNotification({ visible: true, title: "Device Added", message: "1 Device has been added" });
        setTimeout(() => {
          setNotification({ visible: false, title: "", message: "" });
        }, 2000);
      }
    } catch (err) {
      setErrorMessage({ open: true, title: currentForm.mode === "edit" ? "Error Updating Device" : "Error Adding Device", message: err.message });
    } finally {
      setCurrentForm(null);
    }
  }

  function handleCancelFormModal() {
    setCurrentForm(null);
  }

  function openDeleteModal(device) {
    setDeleteData({ open: true, deviceId: device.id });
  }

  async function confirmDelete() {
    try {
      await devicesApi.deleteEntity(deleteData.deviceId);
      setDevices((prevDevices) =>
        prevDevices.filter((device) => device.id !== deleteData.deviceId)
      );
      setNotification({ visible: true, title: "Device Deleted", message: "1 Device has been deleted" });
      setTimeout(() => {
        setNotification({ visible: false, title: "", message: "" });
      }, 2000);
    } catch (err) {
      setErrorMessage({ open: true, title: "Error Deleting Device", message: err.message })
    } finally {
      setDeleteData({ open: false, deviceId: null });
    }
  }

  function cancelDelete() {
    setDeleteData({ open: false, deviceId: null });
  };

  function dismissInformationalModal() {
    setErrorMessage({ open: false, title: "", message: "" })
  }

  if (isLoading) {
    return <Loading />;
  }

  const displayError = error
    ? error.split("\n").map((line, index) => (
      <React.Fragment key={index}>
        {line}
        <br />
      </React.Fragment>
    ))
    : "Ooops! No devices found";

  return (
    <>
      {devices.length === 0 && (
        <div className="d-flex flex-column align-items-center">
          <WarningAlert message={displayError} />
          <br />
          <div className="d-flex justify-content-center">
            <Button
              color="primary"
              onClick={(e) => {
                e.currentTarget.blur();
                setError(null);
                handleAdd();
              }}
              aria-label={error ? "Create First Device" : "Create Device"}
            >
              Create
            </Button>
          </div>
        </div>
      )}

      {devices.length > 0 && (
        <div className="mb-5">
          <div className="d-flex justify-content-end align-items-center mb-2">
            <div style={{ marginRight: "1rem" }}>
              <Button
                color="transparent"
                onClick={(e) => {
                  e.currentTarget.blur();
                  handleAdd();
                }}
                aria-label="Add Device"
              >
                <FontAwesomeIcon icon="plus" />
              </Button>
            </div>
            <div>
              <SearchBar searchTerm={searchTerm} onSearchChange={setSearchTerm} />
            </div>
          </div>

          {filteredDevices.length > 0 ? (
            <>
              <Table bordered hover responsive size="sm" striped>
                <thead>
                  <tr>
                    <SortableHeaderCell columnKey={"customer_id"} label={"Customer_id"} currentSort={sortConfig} onSort={requestSort} />
                    <SortableHeaderCell columnKey={"display_name"} label={"Name"} currentSort={sortConfig} onSort={requestSort} />
                    <SortableHeaderCell columnKey={"id"} label={"Id"} currentSort={sortConfig} onSort={requestSort} />
                    <SortableHeaderCell columnKey={"site_id"} label={"Site_id"} currentSort={sortConfig} onSort={requestSort} />
                    <SortableHeaderCell columnKey={"device_type"} label={"Device_type"} currentSort={sortConfig} onSort={requestSort} />
                    <SortableHeaderCell columnKey={"brand_and_family"} label={"Brand_and_family"} currentSort={sortConfig} onSort={requestSort} />
                    <SortableHeaderCell columnKey={"model"} label={"Model"} currentSort={sortConfig} onSort={requestSort} />
                    <SortableHeaderCell columnKey={"serial_nr"} label={"Serial_nr"} currentSort={sortConfig} onSort={requestSort} />
                    <SortableHeaderCell columnKey={"local_ip"} label={"Local_ip"} currentSort={sortConfig} onSort={requestSort} />
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentDevices.map((device) => (
                    <EntityRow
                      key={device.id}
                      entity={device}
                      fields={["customer_id", "display_name", "id", "site_id", "device_type", "brand_and_family", "model", "serial_nr", "local_ip"]}
                      onEdit={handleEdit}
                      onDelete={openDeleteModal}
                      isSelected={
                        (currentForm && currentForm.mode === "edit" && device.id === currentForm.data.id) ||
                        device.id === deleteData.deviceId
                      }
                      renderField={(field, value) => {
                        if (field === "customer_id") {
                          return <Link to={`/customerDetails/${value}`}>{value.substring(0, 4)}</Link>;
                        }
                        return value;
                      }}
                    />
                  ))}
                </tbody>
              </Table>
              <div className="d-flex justify-content-between align-items-center">
                <PaginationControls currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
                <ItemsPerPageSelector
                  items={itemsPerPage}
                  currentSelection={devicesPerPage}
                  onSelect={handleDropdownChange}
                  isOpen={dropdownOpen}
                  toggle={toggleDropdown}
                />
              </div>
            </>
          ) : (
            <WarningAlert message="Ooops! No devices found" />
          )}
        </div>
      )}

      <InformationalModal
        isOpen={errorMessage.open} title={errorMessage.title} message={errorMessage.message}
        onCancel={dismissInformationalModal}
      />

      <ConfirmationModal
        isOpen={deleteData.open} title="Delete Device?" message="Are you sure you want to delete this device?"
        onConfirm={confirmDelete} onCancel={cancelDelete} confirmLabel="OK" cancelLabel="Cancel"
      />

      <SimpleFormModal
        isOpen={!!currentForm} title={currentForm ? (currentForm.mode === "edit" ? "Edit Device" : "Add Device") : ""}
        initialData={currentForm ? currentForm.data : null}
        fields={deviceFields(deviceType)}
        onSave={handleSaveForm} onCancel={handleCancelFormModal} defaultValues={deviceDefaultValues}
        onFieldChange={(name, value) => {
          if (name === "device_type") setDeviceType(value);
        }}
      />

      {notification.visible && (
        <NotificationToast icon="exclamation-triangle" title={notification.title} message={notification.message} fade={true} />
      )}
    </>
  );
};

export default withAuthenticationRequired(DevicesComponent, {
  onRedirecting: () => <Loading />,
});
